import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useReducer, useState } from 'react';

import {
  GET_SETTINGS_GROUP_ADMINS,
  type SettingsAdminGroup,
  type SettingsGroup,
} from '../../../../queries/skill-administration';

export function useSettingsAdmins(roots: readonly SettingsGroup[]) {
  const client = useApolloClient();
  const [attempt, retry] = useReducer((value: number) => value + 1, 0);
  const request = useMemo(() => ({ roots, attempt }), [roots, attempt]);
  const [snapshot, setSnapshot] = useState<{
    request: object;
    groups: SettingsAdminGroup[];
    error: boolean;
  }>();

  useEffect(() => {
    if (!roots.length) return;
    let active = true;
    // Load profiles only on Admins entry, scoped to roots already confirmed for this session.
    void Promise.all(
      roots.map(({ uuid }) =>
        client
          .query({
            query: GET_SETTINGS_GROUP_ADMINS,
            variables: { groupId: uuid },
            fetchPolicy: 'no-cache',
            context: { queryDeduplication: false },
          })
          .then(({ data }) => data.getSkillGroup),
      ),
    ).then(
      (groups) => {
        if (active) setSnapshot({ request, groups, error: false });
      },
      () => {
        if (active) setSnapshot({ request, groups: [], error: true });
      },
    );
    return () => {
      active = false;
    };
  }, [client, request, roots]);

  const current = snapshot?.request === request ? snapshot : undefined;
  const admins = useMemo(
    () =>
      (current?.groups ?? []).flatMap((group) => {
        if (group.parentGroupUuid !== null) return [];
        const employees = new Map((group.adminEmployees ?? []).map((employee) => [employee.userUuid, employee]));
        return [...new Set(group.admins)].map((userUuid) => ({
          groupUuid: group.uuid,
          organization: group.name,
          userUuid,
          employee: employees.get(userUuid),
        }));
      }),
    [current],
  );

  return {
    admins,
    loading: roots.length > 0 && !current,
    error: current?.error ?? false,
    retry,
    updateAdmins: (groupUuid: string, userUuids: string[]) => {
      setSnapshot((previous) =>
        previous?.request === request
          ? {
              ...previous,
              groups: previous.groups.map((group) =>
                group.uuid === groupUuid ? { ...group, admins: userUuids } : group,
              ),
            }
          : previous,
      );
    },
  };
}
